/**
 * Option de montage des pneus — vanilla JS, zéro dépendance, même esprit que devis-widget.js et
 * pneus-resultats.js. Chargé sur TOUTES les pages par snippets/mauto-montage-pneu.liquid.
 *
 * Le client choisit entre deux options, sur la fiche produit comme sur la page résultats :
 *   « Montage à l'atelier » (par défaut) → le pneu + le produit « Montage pneu », même quantité
 *   « Retrait seul »                     → le pneu seul, il le montera ailleurs
 *
 * Le choix voyage avec chaque ligne de panier, dans une propriété de ligne Shopify (« Prestation »,
 * cf. PROPRIETE ci-dessous). C'est ce qui permet de le retrouver plus tard : deux lignes du même
 * pneu avec des choix différents restent deux lignes distinctes, la propriété s'affiche telle
 * quelle dans le panier, dans la commande et sur le bon de commande de l'atelier.
 *
 * Ce fichier expose deux choses :
 *   1. window.MautoPneu — les libellés, le prix du montage et le calcul de la date d'intervention,
 *      utilisés aussi par assets/pneus-resultats.js et par les blocs de la fiche produit, pour que
 *      les trois surfaces ne puissent pas diverger ;
 *   2. un gardien du panier : la quantité de la ligne « Montage pneu » vaut toujours le total des
 *      pneus DONT LE CHOIX EST LE MONTAGE, et la ligne disparaît quand il n'y en a plus aucun.
 *
 * Le client peut aussi changer d'avis depuis la page panier : chaque ligne de pneu y reçoit un
 * bouton qui bascule d'une option à l'autre (cf. ajouterBasculeurs). Le choix courant est déjà
 * affiché par le thème — la propriété de ligne apparaît sous le titre du produit — donc on
 * n'ajoute qu'un bouton d'action, sans dupliquer l'information.
 *
 * Comment le gardien est déclenché : le thème Dawn modifie le panier en arrière-plan (sans
 * recharger la page) via /cart/add.js, /cart/change.js, /cart/update.js. On habille donc
 * `window.fetch` pour repasser derrière chacun de ces appels. Deux garde-fous contre la boucle :
 *   1. nos propres appels passent par `fetchNatif`, mémorisé AVANT l'habillage — ils ne se
 *      redéclenchent jamais eux-mêmes ;
 *   2. le rechargement de la page panier (nécessaire pour que les totaux affichés soient justes)
 *      est plafonné à 3 fois par tranche de 15 secondes, cf. rechargerUneFois.
 */
(function () {
  'use strict';

  var cfg = window.MautoMontagePneuReglages || {};

  // Nom de la propriété de ligne et libellés des deux options. Ils sont VISIBLES par le client
  // (panier, e-mail de confirmation, commande) : les changer change ce que lit l'atelier sur les
  // commandes déjà passées. Ils doivent rester identiques partout, d'où leur définition unique ici.
  var PROPRIETE = 'Prestation';
  var LIBELLE_MONTAGE = 'Montage à l\'atelier';
  var LIBELLE_RETRAIT = 'Retrait seul';
  var LIEU = 'Pont-Saint-Martin (44860)';

  // Deuxième propriété de ligne : la disponibilité annoncée. Elle voyage avec la commande, donc
  // le client la retrouve dans le panier, dans la commande et dans l'e-mail — et l'atelier aussi.
  var PROPRIETE_DISPO = 'Disponibilité';

  var VARIANT_MONTAGE = cfg.variantId ? String(cfg.variantId) : '';
  var PRIX_MONTAGE_CENTS = Number(cfg.prixCents) || 0;
  var CLE_RECHARGEMENT = 'mautoMontageRechargements';
  var CHEMINS_PANIER = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // Mention FIXE, volontairement : pas de date calculée. Une date calculée le jour de la visite
  // se décale de celle que Shopify calcule le jour de la commande, et périme si le client hésite
  // avant de payer. Le délai de retrait Shopify est réglé sur « 5 jours ou plus », les deux
  // disent donc la même chose. Ce n'est pas une prise de rendez-vous : le créneau se fixe ensuite.
  function texteDisponibilite(avecMontage) {
    return (avecMontage ? 'Disponible sous 5 jours ouvrés' : 'Disponible au retrait sous 5 jours ouvrés') + ' — ' + LIEU;
  }

  function formatEuro(cents) {
    try {
      return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  window.MautoPneu = {
    propriete: PROPRIETE,
    proprieteDispo: PROPRIETE_DISPO,
    libelleMontage: LIBELLE_MONTAGE,
    libelleRetrait: LIBELLE_RETRAIT,
    lieu: LIEU,
    variantIdMontage: VARIANT_MONTAGE,
    prixMontageCents: PRIX_MONTAGE_CENTS,
    texteDisponibilite: texteDisponibilite,
    formatEuro: formatEuro,
  };

  // Produit « Montage pneu » absent ou dépublié : les libellés et la date restent disponibles
  // ci-dessus (les deux options s'affichent donc toujours), mais on ne touche plus au panier.
  if (!VARIANT_MONTAGE) return;

  var fetchNatif = window.fetch ? window.fetch.bind(window) : null;
  if (!fetchNatif) return; // navigateur sans fetch : le panier reste utilisable, sans automatisme
  var enCours = false;

  function surPagePanier() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  // Un pneu = un produit dont le type contient « pneu ». Volontairement tolérant (casse, libellé
  // du genre « Pneu tourisme »), mais la ligne montage elle-même est toujours exclue.
  function estPneu(item) {
    if (String(item.variant_id) === VARIANT_MONTAGE) return false;
    return String(item.product_type || '').toLowerCase().indexOf('pneu') !== -1;
  }

  // Absence de propriété = montage, puisque c'est l'option cochée par défaut. Cas de figure : un
  // pneu ajouté depuis une surface qui ne pose pas la propriété (recherche du thème, lien direct).
  function aChoisiLeMontage(item) {
    var choix = (item.properties || {})[PROPRIETE];
    if (!choix) return true;
    return String(choix) === LIBELLE_MONTAGE;
  }

  function lirePanier() {
    return fetchNatif('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (reponse) { return reponse.json(); });
  }

  function poster(url, corps) {
    return fetchNatif(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(corps),
    }).then(function (reponse) { return reponse.ok; });
  }

  // Renvoie true si le panier a réellement été modifié (donc si l'affichage est à rafraîchir).
  function synchroniser() {
    if (enCours) return Promise.resolve(false);
    enCours = true;
    return lirePanier().then(function (panier) {
      var items = panier.items || [];
      var qteAMonter = 0;
      var ligneMontage = null;
      var indexMontage = -1;
      items.forEach(function (item, i) {
        if (String(item.variant_id) === VARIANT_MONTAGE) { ligneMontage = item; indexMontage = i; return; }
        if (estPneu(item) && aChoisiLeMontage(item)) qteAMonter += item.quantity;
      });
      var qteMontage = ligneMontage ? ligneMontage.quantity : 0;

      if (qteAMonter === 0) {
        // Plus rien à monter : la ligne montage disparaît (quantité 0 = suppression).
        if (!ligneMontage) return false;
        return poster('/cart/change.js', { id: ligneMontage.key, quantity: 0 });
      }
      if (!ligneMontage) {
        return poster('/cart/add.js', { items: [{ id: VARIANT_MONTAGE, quantity: qteAMonter }] });
      }
      // Remarque (vérifiée en conditions réelles) : l'ordre des lignes du panier n'est PAS
      // pilotable. Retirer la ligne montage puis la réajouter la replace exactement au même rang.
      // L'ordre est donc corrigé à l'affichage seulement, cf. remonterMontageEnDernier().
      if (qteMontage === qteAMonter) return false;
      // `key` et non l'identifiant de déclinaison : c'est la seule adresse fiable d'une ligne.
      return poster('/cart/change.js', { id: ligneMontage.key, quantity: qteAMonter });
    }).catch(function () {
      return false; // panier injoignable : on ne bloque jamais la navigation du client
    }).then(function (modifie) {
      enCours = false;
      return modifie;
    });
  }

  // Garde-fou anti-boucle : 3 rechargements au maximum par tranche de 15 secondes. Si une
  // divergence ne se résorbait pas (cas non prévu), le client garde une page utilisable au lieu
  // d'une page qui se recharge sans fin.
  function rechargerUneFois() {
    var maintenant = Date.now();
    var etat = { debut: maintenant, nombre: 0 };
    try {
      var brut = window.sessionStorage.getItem(CLE_RECHARGEMENT);
      if (brut) {
        var lu = JSON.parse(brut);
        if (lu && maintenant - lu.debut < 15000) etat = lu;
      }
    } catch (e) { /* sessionStorage indisponible : on recharge, sans compteur */ }
    if (etat.nombre >= 3) return;
    etat.nombre += 1;
    try { window.sessionStorage.setItem(CLE_RECHARGEMENT, JSON.stringify(etat)); } catch (e) { /* ignoré */ }
    window.location.reload();
  }

  // Hors page panier, on ne recharge pas : on remet seulement à jour la pastille du panier de
  // Dawn, sinon elle afficherait un article de moins que la réalité juste après l'ajout du
  // montage. Passe par l'API de rendu de section de Shopify, comme le fait le thème lui-même.
  function rafraichirPastillePanier() {
    var pastille = document.getElementById('cart-icon-bubble');
    if (!pastille) return;
    fetchNatif('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections && sections['cart-icon-bubble'];
        if (!html) return;
        var enveloppe = document.createElement('div');
        enveloppe.innerHTML = html;
        // La section rend le CONTENU du lien, pas un élément portant l'identifiant
        // « cart-icon-bubble » : le chercher ici ne trouvait jamais rien, et la pastille n'a donc
        // jamais été rafraîchie depuis l'écriture de ce fichier. Cf. la même correction dans
        // assets/mauto-parcours-achat.js — les deux doivent rester alignées.
        var enveloppeSection = enveloppe.querySelector('#shopify-section-cart-icon-bubble');
        var contenu = enveloppeSection ? enveloppeSection.innerHTML : enveloppe.innerHTML;
        if (contenu && contenu.trim()) pastille.innerHTML = contenu;
      })
      .catch(function () { /* pastille non rafraîchie : sans conséquence sur le panier réel */ });
  }

  function synchroniserPuisRafraichir() {
    return synchroniser().then(function (modifie) {
      if (!modifie) return false;
      if (surPagePanier()) rechargerUneFois();
      else rafraichirPastillePanier();
      return true;
    });
  }

  // Page panier : la ligne montage n'est pas modifiable par le client (ni quantité, ni retrait),
  // puisqu'elle est entièrement pilotée par les pneus à monter. On remplace donc la cellule
  // quantité de Dawn (champ + bouton retirer) par la quantité en clair et son explication.
  // Idempotent : l'attribut posé sur la ligne évite de repasser dessus à chaque re-rendu.
  function verrouillerLigneMontage() {
    if (!surPagePanier() || !cfg.handle) return;
    var lignes = document.querySelectorAll('.cart-item');
    for (var i = 0; i < lignes.length; i++) {
      var ligne = lignes[i];
      if (ligne.getAttribute('data-mauto-montage') === 'fige') continue;
      if (!ligne.querySelector('a[href*="/products/' + cfg.handle + '"]')) continue;
      var cellule = ligne.querySelector('.cart-item__quantity');
      if (!cellule) continue;
      var champ = cellule.querySelector('.quantity__input');
      var quantite = champ ? champ.value : '';
      ligne.setAttribute('data-mauto-montage', 'fige');
      cellule.innerHTML =
        '<div class="mauto-montage-fige">' +
          '<strong>' + (quantite ? '× ' + quantite : '') + '</strong>' +
          '<span>Suit automatiquement les pneus à monter</span>' +
        '</div>';
    }
  }

  // Page panier : chaque ligne de pneu reçoit un bouton pour changer d'avis, dans un sens comme
  // dans l'autre. Changer l'option revient à changer la propriété de la ligne — Shopify fusionne
  // alors tout seul deux lignes du même pneu qui se retrouveraient avec le même choix.
  var basculeEnCours = false;

  function poserBascule(ligne, item) {
    if (ligne.getAttribute('data-mauto-bascule') === 'pose') return;
    if (!estPneu(item)) return;
    var details = ligne.querySelector('.cart-item__details');
    if (!details) return;
    ligne.setAttribute('data-mauto-bascule', 'pose');

    var versMontage = !aChoisiLeMontage(item);
    var bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.className = 'mauto-cart-bascule';
    bouton.textContent = versMontage
      ? 'Ajouter le montage' + (PRIX_MONTAGE_CENTS ? ' (+ ' + formatEuro(PRIX_MONTAGE_CENTS) + ' / pneu)' : '')
      : 'Passer en retrait seul (sans montage)';

    bouton.addEventListener('click', function () {
      if (basculeEnCours) return;
      basculeEnCours = true;
      bouton.disabled = true;
      var proprietes = {};
      proprietes[PROPRIETE] = versMontage ? LIBELLE_MONTAGE : LIBELLE_RETRAIT;
      // `quantity` est renvoyée telle quelle : sans elle, /cart/change.js remettrait la ligne à 1.
      poster('/cart/change.js', { id: item.key, quantity: item.quantity, properties: proprietes })
        .then(function (ok) {
          // La ligne montage est recalée AVANT le rechargement : sinon la page se rechargerait une
          // première fois avec un montage faux, puis une seconde fois une fois corrigé.
          return ok ? synchroniser() : false;
        })
        .then(function () { rechargerUneFois(); })
        .catch(function () { /* rien changé : on rend la main au client */ })
        .then(function () {
          basculeEnCours = false;
          bouton.disabled = false;
        });
    });

    // Lien explicite vers la fiche produit. Le titre de la ligne est déjà cliquable dans Dawn,
    // mais rien ne le signale (ni couleur, ni soulignement) : le client ne devine pas qu'il peut
    // retourner voir le pneu. Ce lien-ci se voit.
    if (item.url) {
      var fiche = document.createElement('a');
      fiche.className = 'mauto-cart-fiche';
      fiche.href = item.url;
      fiche.textContent = 'Voir la fiche du pneu';
      details.appendChild(fiche);
    }

    details.appendChild(bouton);
  }

  // Un seul relevé du panier à la fois : poser les boutons modifie le tableau, ce qui réveille
  // l'observateur ci-dessous, qui rappelle cette fonction. Sans ce drapeau, chaque re-rendu du
  // panier déclencherait une rafale d'appels à /cart.js.
  var basculesEnCalcul = false;

  // Rang de la ligne dans le panier, lu sur le marquage de Dawn (`data-index`, 1 pour la première)
  // et NON sur la position à l'écran : remonterMontageEnDernier() déplace les lignes, l'ordre
  // affiché ne correspond donc plus à celui de /cart.js. Se fier à la position collerait le bouton
  // « Passer en retrait seul » sur la mauvaise ligne — c'est arrivé, d'où ce détour.
  function indexLigne(ligne) {
    var marque = ligne.querySelector('[data-index]');
    if (marque) {
      var n = parseInt(marque.getAttribute('data-index'), 10);
      if (n > 0) return n - 1;
    }
    var champ = ligne.querySelector('.quantity__input');
    if (champ && champ.id) {
      var m = champ.id.match(/(\d+)$/);
      if (m) return parseInt(m[1], 10) - 1;
    }
    return -1;
  }

  function ajouterBasculeurs() {
    if (!surPagePanier() || basculesEnCalcul) return;
    var lignes = document.querySelectorAll('.cart-item');
    if (!lignes.length) return;
    basculesEnCalcul = true;
    lirePanier().then(function (panier) {
      var items = panier.items || [];
      if (items.length !== lignes.length) return; // affichage désynchronisé : on s'abstient
      for (var i = 0; i < lignes.length; i++) {
        var rang = indexLigne(lignes[i]);
        // Rang illisible : on ne devine pas, la ligne reste simplement sans bouton.
        if (rang < 0 || !items[rang]) continue;
        poserBascule(lignes[i], items[rang]);
      }
    }).catch(function () { /* panier injoignable : pas de bouton, rien de cassé */ })
      .then(function () { basculesEnCalcul = false; });
  }

  // Le montage se lit après les pneus, jamais avant. L'ordre des lignes du panier étant imposé par
  // Shopify (voir la remarque dans synchroniser), on le corrige ici, à l'affichage : la ligne
  // montage est déplacée en fin de tableau. Idempotent, et sans effet si elle y est déjà.
  function remonterMontageEnDernier() {
    if (!surPagePanier() || !cfg.handle) return;
    var lignes = document.querySelectorAll('.cart-item');
    if (lignes.length < 2) return;
    var montage = null;
    for (var i = 0; i < lignes.length; i++) {
      if (lignes[i].querySelector('a[href*="/products/' + cfg.handle + '"]')) { montage = lignes[i]; break; }
    }
    if (!montage || montage === lignes[lignes.length - 1]) return;
    if (montage.parentNode) montage.parentNode.appendChild(montage);
  }

  // ── Rendez-vous de montage ──────────────────────────────────────────────────────────────────
  // Le client choisit sa date et son créneau ICI, sur la page panier, et non sur la fiche : une
  // commande = un rendez-vous. Le choix part en attributs de commande (/cart/update.js), que
  // Shopify transmet dans `note_attributes` — c'est ce que lit le webhook orders/paid du
  // sync-server pour créer l'OR « à confirmer » APRÈS paiement. Rien n'est réservé avant : un
  // panier abandonné ne bloque donc aucun créneau.
  //
  // Les règles de disponibilité viennent de assets/mauto-calendrier.js, le même fichier que le
  // widget de devis/RDV — il n'existe qu'un seul calendrier. Seul le délai change : 5 jours
  // ouvrés au lieu de 48 h, le temps que le fournisseur livre les pneus.
  var ATTR_LISIBLE = 'Rendez-vous de montage';
  var ATTR_DATE = '_rdv_date';
  var ATTR_CRENEAU = '_rdv_creneau';
  // Véhicule : mêmes trois champs, même format et mêmes messages d'erreur que le widget de
  // devis/RDV (devis-rdv-widget.js, renderStepVehicule), pour que l'OR créé à partir d'une
  // commande de pneus soit identique à celui d'une demande de rendez-vous classique.
  var ATTR_VEHICULE = 'Véhicule';
  var ATTR_IMMAT = '_rdv_immat';
  var ATTR_MARQUE = '_rdv_marque';
  var ATTR_MODELE = '_rdv_modele';

  var IMMAT_REGEX = /^[A-Za-z]{2}[\s-]?\d{3}[\s-]?[A-Za-z]{2}$/;
  function normaliserImmat(v) {
    var compact = String(v || '').replace(/[\s-]/g, '').toUpperCase();
    if (compact.length === 7) return compact.slice(0, 2) + '-' + compact.slice(2, 5) + '-' + compact.slice(5, 7);
    return String(v || '').trim().toUpperCase();
  }

  var fermeturesChargees = null;
  var selecteurEnPlace = false;

  function calendrier() { return window.MautoCalendrier || null; }

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function lireVehicule(bloc, choix) {
    var champs = bloc.querySelectorAll('[data-champ]');
    for (var i = 0; i < champs.length; i++) {
      choix[champs[i].getAttribute('data-champ')] = champs[i].value.trim();
    }
    if (choix.immat) choix.immat = normaliserImmat(choix.immat);
  }

  function chargerFermeturesUneFois() {
    if (fermeturesChargees) return fermeturesChargees;
    var cal = calendrier();
    fermeturesChargees = cal ? cal.chargerFermetures(cfg.apiBase) : Promise.resolve([]);
    return fermeturesChargees;
  }

  // Ce que le PANIER porte réellement, et non ce que l'écran affiche. C'est l'écart entre les deux
  // qui a coûté la commande #1006 : le sélecteur montrait un rendez-vous complet, le bouton de
  // paiement était actif, et le panier parti au paiement n'avait aucun attribut _rdv_. Le serveur
  // l'a donc écartée en « ignorée (pas de créneau de montage) ».
  // La signature est vide tant qu'un des cinq champs manque : un rendez-vous à moitié écrit ne
  // vaut rien pour le serveur.
  function signatureAttributs(attrs) {
    attrs = attrs || {};
    var valeurs = [attrs[ATTR_DATE], attrs[ATTR_CRENEAU], attrs[ATTR_IMMAT], attrs[ATTR_MARQUE], attrs[ATTR_MODELE]];
    return valeurs.every(Boolean) ? valeurs.join('|') : '';
  }

  function signatureChoix(c) {
    if (!c || !c.date || !c.creneau || !c.immat || !c.marque || !c.modele) return '';
    return [c.date, c.creneau, normaliserImmat(c.immat), c.marque, c.modele].join('|');
  }

  // Mise à jour à chaque réponse de Shopify, jamais à l'envoi : seule la réponse dit ce qui a
  // été retenu. Null = pas encore lu.
  var signatureEnregistree = null;
  // Rappelé après chaque écriture (réussie ou non) pour recalculer l'état du bouton de paiement.
  var apresEcritureRdv = null;
  var ecritureRdvEchouee = false;
  // Écritures envoyées ou en attente dans la file : tant qu'il y en a, l'état du panier va encore
  // changer et il est inutile d'en relancer une.
  var ecrituresRdvEnFile = 0;

  // Les écritures partent EN FILE, jamais en parallèle. Choisir une date efface le rendez-vous
  // (le créneau n'est plus valable), choisir le créneau juste après le réécrit : deux appels
  // /cart/update.js lancés coup sur coup ne reviennent pas forcément dans l'ordre, et si
  // l'effacement arrive en dernier le panier se retrouve vide sous un écran complet.
  var fileRdv = Promise.resolve();

  // Un seul écrit pour tout le rendez-vous : créneau ET véhicule. Chaîne vide = attribut
  // supprimé côté Shopify, ce qui permet de tout effacer d'un coup.
  function enregistrerRdv(choix) {
    var cal = calendrier();
    var complet = choix && choix.date && choix.creneau && choix.immat && choix.marque && choix.modele;
    var attributs = {};
    if (complet) {
      attributs[ATTR_LISIBLE] = (cal ? cal.formatLong(choix.date) : choix.date) + ' — ' + choix.creneau;
      attributs[ATTR_DATE] = choix.date;
      attributs[ATTR_CRENEAU] = choix.creneau;
      attributs[ATTR_VEHICULE] = choix.immat + ' — ' + choix.marque + ' ' + choix.modele;
      attributs[ATTR_IMMAT] = choix.immat;
      attributs[ATTR_MARQUE] = choix.marque;
      attributs[ATTR_MODELE] = choix.modele;
    } else {
      [ATTR_LISIBLE, ATTR_DATE, ATTR_CRENEAU, ATTR_VEHICULE, ATTR_IMMAT, ATTR_MARQUE, ATTR_MODELE]
        .forEach(function (k) { attributs[k] = ''; });
    }
    ecrituresRdvEnFile += 1;
    fileRdv = fileRdv.then(function () {
      return fetchNatif('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attributs }),
      }).then(function (reponse) {
        if (!reponse.ok) throw new Error('update.js ' + reponse.status);
        return reponse.json();
      }).then(function (panier) {
        signatureEnregistree = signatureAttributs(panier.attributes);
        ecritureRdvEchouee = false;
        return true;
      }).catch(function () {
        ecritureRdvEchouee = true;
        return false;
      }).then(function (ok) {
        ecrituresRdvEnFile -= 1;
        if (apresEcritureRdv) apresEcritureRdv();
        return ok;
      });
    });
    return fileRdv;
  }

  // Les boutons de paiement accéléré (Shop Pay, PayPal, Apple Pay…) mènent DIRECTEMENT au
  // paiement, sans repasser par le bouton « Procéder au paiement ». Les laisser actifs pendant
  // qu'on bloque le bouton standard ne bloque donc rien du tout : le client paie ses pneus sans
  // date, sans créneau et sans immatriculation, la commande arrive sans aucun attribut _rdv_, et
  // le serveur la journalise en « ignorée (pas de créneau de montage) ». Le client a payé et
  // personne ne l'attend.
  //
  // Ils vivent dans un DOM masqué (shopify-accelerated-checkout) : on ne peut pas les désactiver
  // un par un de façon fiable. On masque donc le conteneur entier, ce qui vaut aussi pour les
  // moyens de paiement que Shopify ajouterait demain.
  // On pose une classe sur <html> plutôt que de masquer chaque boîte en JavaScript : ces boutons
  // sont des composants web montés APRÈS le chargement, et souvent re-montés quand le panier
  // change. Un masquage posé au mauvais instant les manquerait. Une règle CSS, elle, s'applique
  // aussi à ce qui apparaît plus tard. La règle vit dans snippets/mauto-montage-pneu.liquid.
  var CLASSE_BLOCAGE = 'mauto-rdv-incomplet'

  function majPaiementRapide(actif) {
    var racine = document.documentElement
    if (actif) racine.classList.remove(CLASSE_BLOCAGE)
    else racine.classList.add(CLASSE_BLOCAGE)
  }

  function majBoutonPaiement(actif, message) {
    var boutons = document.querySelectorAll('[name="checkout"], .cart__checkout-button');
    for (var i = 0; i < boutons.length; i++) {
      boutons[i].disabled = !actif;
      boutons[i].setAttribute('aria-disabled', actif ? 'false' : 'true');
    }
    majPaiementRapide(actif);
    var avis = document.querySelector('[data-mauto-rdv-avis]');
    if (avis) {
      avis.textContent = message || '';
      avis.hidden = !message;
    }
  }

  function poserSelecteurRdv(panier) {
    if (!surPagePanier() || selecteurEnPlace) return;
    var cal = calendrier();
    var aMonter = (panier.items || []).some(function (it) { return estPneu(it) && aChoisiLeMontage(it); });
    var hote = document.querySelector('.cart__footer') || document.querySelector('#main-cart-footer');
    if (!hote) return;

    if (!aMonter) {
      // Plus rien à monter : on efface un créneau éventuellement resté d'un panier précédent.
      if ((panier.attributes || {})[ATTR_DATE]) enregistrerRdv(null);
      majBoutonPaiement(true, '');
      return;
    }
    if (!cal) {
      // Sans les règles de disponibilité, on n'invente pas de dates : le client passe commande
      // et l'atelier le rappelle. Mieux vaut ça qu'un rendez-vous faux.
      majBoutonPaiement(true, '');
      return;
    }

    selecteurEnPlace = true;
    var bloc = document.createElement('div');
    bloc.className = 'mauto-rdv-panier';
    bloc.setAttribute('data-mauto-rdv', '');
    hote.parentNode.insertBefore(bloc, hote);

    var attrs = panier.attributes || {};
    var choix = {
      date: attrs[ATTR_DATE] || '', creneau: attrs[ATTR_CRENEAU] || '',
      immat: attrs[ATTR_IMMAT] || '', marque: attrs[ATTR_MARQUE] || '', modele: attrs[ATTR_MODELE] || '',
    };
    signatureEnregistree = signatureAttributs(attrs);
    // Tant que les règles de disponibilité ne sont pas chargées, rien n'est encore affiché : le
    // paiement reste fermé, cf. le verrou de soumission ci-dessous.
    var paiementAutorise = function () { return false; };

    // Dernier rempart : même si le bouton était réactivé par un autre script (Dawn redessine le
    // pied du panier après chaque changement de quantité), le formulaire ne part pas au paiement
    // tant que le panier ne porte pas le rendez-vous affiché. Phase de capture, pour passer avant
    // les gestionnaires du thème.
    document.addEventListener('submit', function (ev) {
      var formulaire = ev.target;
      if (!formulaire || formulaire.id !== 'cart') return;
      if (paiementAutorise()) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }, true);

    chargerFermeturesUneFois().then(function (fermetures) {
      var dateMin = cal.dateMinParJoursOuvres(cal.RDV_DELAI_JOURS_OUVRES);
      var vue = choix.date ? new Date(choix.date + 'T00:00:00') : new Date(dateMin.getTime());
      var anneeVue = vue.getFullYear(), moisVue = vue.getMonth();

      function dessiner() {
        var premier = new Date(anneeVue, moisVue, 1);
        var decalage = (premier.getDay() + 6) % 7; // grille française, lundi en tête
        var nbJours = new Date(anneeVue, moisVue + 1, 0).getDate();
        var jours = '';
        for (var v = 0; v < decalage; v++) jours += '<span class="mauto-rdv-jour is-vide"></span>';
        for (var j = 1; j <= nbJours; j++) {
          var d = new Date(anneeVue, moisVue, j);
          var s = cal.versChaine(d);
          var ok = cal.estSelectionnable(d, dateMin, fermetures);
          jours += '<button type="button" class="mauto-rdv-jour' + (ok ? '' : ' is-grise') +
            (choix.date === s ? ' is-choisi' : '') + '"' +
            (ok ? ' data-date="' + s + '"' : ' disabled') + '>' + j + '</button>';
        }
        var moisPrecedentPossible = new Date(anneeVue, moisVue, 1) > new Date(dateMin.getFullYear(), dateMin.getMonth(), 1);
        bloc.innerHTML =
          '<p class="mauto-rdv-titre">Choisissez votre rendez-vous de montage</p>' +
          '<p class="mauto-rdv-note">Vos pneus sont commandés dès le paiement : le premier rendez-vous possible est dans 5 jours ouvrés.</p>' +
          '<div class="mauto-rdv-nav">' +
            '<button type="button" data-mois="-1"' + (moisPrecedentPossible ? '' : ' disabled') + '>‹</button>' +
            '<span>' + cal.MOIS[moisVue] + ' ' + anneeVue + '</span>' +
            '<button type="button" data-mois="1">›</button>' +
          '</div>' +
          '<div class="mauto-rdv-entetes">' + cal.JOURS_COURTS.map(function (x) { return '<span>' + x + '</span>'; }).join('') + '</div>' +
          '<div class="mauto-rdv-grille">' + jours + '</div>' +
          '<div class="mauto-rdv-creneaux">' +
            cal.CRENEAUX.map(function (c) {
              return '<button type="button" class="mauto-rdv-creneau' + (choix.creneau === c ? ' is-choisi' : '') + '" data-creneau="' + c + '"' + (choix.date ? '' : ' disabled') + '>' + c + '</button>';
            }).join('') +
          '</div>' +
          // Mêmes trois champs que le widget de devis/RDV, dans le même ordre et avec les mêmes
          // libellés : l'atelier retrouve exactement les mêmes informations sur l'OR.
          '<div class="mauto-rdv-vehicule">' +
            '<p class="mauto-rdv-sous-titre">Votre véhicule</p>' +
            '<label for="mauto-rdv-immat">Immatriculation *</label>' +
            '<input type="text" id="mauto-rdv-immat" data-champ="immat" placeholder="Ex. AB-123-CD" value="' + escHtml(choix.immat) + '" required>' +
            '<label for="mauto-rdv-marque">Marque *</label>' +
            '<input type="text" id="mauto-rdv-marque" data-champ="marque" placeholder="Ex. Renault" value="' + escHtml(choix.marque) + '" required>' +
            '<label for="mauto-rdv-modele">Modèle *</label>' +
            '<input type="text" id="mauto-rdv-modele" data-champ="modele" placeholder="Ex. Clio" value="' + escHtml(choix.modele) + '" required>' +
          '</div>' +
          '<p class="mauto-rdv-recap" data-mauto-rdv-avis hidden></p>';

        bloc.querySelectorAll('[data-mois]').forEach(function (b) {
          b.addEventListener('click', function () {
            moisVue += Number(b.getAttribute('data-mois'));
            if (moisVue < 0) { moisVue = 11; anneeVue--; }
            if (moisVue > 11) { moisVue = 0; anneeVue++; }
            dessiner();
          });
        });
        // Dans chaque gestionnaire, l'écriture est mise en file AVANT de redessiner : dessiner()
        // appelle appliquerEtat(), qui relance une écriture s'il n'en voit aucune en cours.
        bloc.querySelectorAll('[data-date]').forEach(function (b) {
          b.addEventListener('click', function () {
            choix.date = b.getAttribute('data-date');
            choix.creneau = '';
            enregistrerRdv(null);
            dessiner();
          });
        });
        bloc.querySelectorAll('[data-creneau]').forEach(function (b) {
          b.addEventListener('click', function () {
            if (!choix.date) return;
            choix.creneau = b.getAttribute('data-creneau');
            enregistrerRdv(choix);
            dessiner();
          });
        });
        // On ne redessine PAS à chaque frappe : cela ferait perdre le curseur. Les champs sont
        // relus à la volée ; l'écriture, elle, est déclenchée par appliquerEtat() dès que le
        // rendez-vous est complet, sans attendre que le client quitte le champ — sinon le bouton
        // de paiement serait actif avec un panier encore vide.
        bloc.querySelectorAll('[data-champ]').forEach(function (champ) {
          champ.addEventListener('input', function () {
            lireVehicule(bloc, choix);
            appliquerEtat();
          });
          champ.addEventListener('blur', function () {
            if (champ.getAttribute('data-champ') === 'immat' && champ.value.trim()) {
              champ.value = normaliserImmat(champ.value);
            }
            lireVehicule(bloc, choix);
            // Un rendez-vous incomplet n'est effacé du panier que s'il y figure encore : sinon
            // cet effacement partirait pour rien, au moment précis où le client clique un créneau.
            if (!estComplet(choix) && (signatureEnregistree || ecrituresRdvEnFile > 0)) enregistrerRdv(null);
            appliquerEtat();
          });
        });
        appliquerEtat();
      }

      function estComplet(c) {
        return !!(c.date && c.creneau && c.marque && c.modele && IMMAT_REGEX.test(c.immat));
      }

      paiementAutorise = function () {
        return estComplet(choix) && signatureChoix(choix) === signatureEnregistree;
      };

      var relanceApresEchec = null;
      apresEcritureRdv = appliquerEtat;

      function appliquerEtat() {
        if (!choix.date) return majBoutonPaiement(false, 'Choisissez une date de rendez-vous pour continuer.');
        if (!choix.creneau) return majBoutonPaiement(false, 'Choisissez un créneau horaire pour continuer.');
        if (!choix.immat || !choix.marque || !choix.modele) {
          return majBoutonPaiement(false, "Renseignez l'immatriculation, la marque et le modèle de votre véhicule.");
        }
        if (!IMMAT_REGEX.test(choix.immat)) {
          return majBoutonPaiement(false, "Format d'immatriculation invalide — utilisez le format AB-123-CD.");
        }
        if (signatureChoix(choix) !== signatureEnregistree) {
          // Écran complet, panier pas encore à jour : le paiement reste fermé jusqu'à ce que
          // Shopify ait confirmé. Une seule écriture à la fois ; les frappes suivantes sont
          // reprises par la réécriture qui suit la confirmation.
          if (ecritureRdvEchouee) {
            if (!relanceApresEchec) {
              relanceApresEchec = setTimeout(function () {
                relanceApresEchec = null;
                ecritureRdvEchouee = false;
                appliquerEtat();
              }, 3000);
            }
            return majBoutonPaiement(false, "Votre rendez-vous n'a pas pu être enregistré — nouvel essai dans quelques secondes. Vérifiez votre connexion.");
          }
          if (ecrituresRdvEnFile === 0) enregistrerRdv(choix);
          return majBoutonPaiement(false, 'Enregistrement de votre rendez-vous…');
        }
        majBoutonPaiement(true, 'Rendez-vous : ' + cal.formatLong(choix.date) + ' — ' + choix.creneau +
          ' · ' + normaliserImmat(choix.immat) + ' ' + choix.marque + ' ' + choix.modele);
      }

      dessiner();
    });
  }

  function rafraichirPagePanier() {
    remonterMontageEnDernier();
    verrouillerLigneMontage();
    ajouterBasculeurs();
    if (surPagePanier() && !selecteurEnPlace) {
      lirePanier().then(poserSelecteurRdv).catch(function () { /* panier injoignable : pas de sélecteur */ });
    }
  }

  // Dawn redessine le tableau du panier après chaque modification : sans cette observation, le
  // verrouillage et les boutons de bascule seraient perdus au premier changement de quantité.
  function observerPanier() {
    if (!surPagePanier() || typeof MutationObserver !== 'function') return;
    var conteneur = document.querySelector('cart-items') || document.getElementById('main-cart-items');
    if (!conteneur) return;
    new MutationObserver(rafraichirPagePanier).observe(conteneur, { childList: true, subtree: true });
  }

  function habillerFetch() {
    var precedent = window.fetch;
    window.fetch = function (entree) {
      var url = '';
      try {
        url = typeof entree === 'string' ? entree : (entree && entree.url) || '';
      } catch (e) { /* Request exotique : on laisse passer sans surveiller */ }
      var promesse = precedent.apply(this, arguments);
      if (!CHEMINS_PANIER.test(url)) return promesse;
      return promesse.then(function (reponse) {
        // setTimeout : le thème doit finir de traiter SA réponse avant qu'on retouche le panier.
        setTimeout(synchroniserPuisRafraichir, 0);
        return reponse;
      });
    };
  }

  function init() {
    habillerFetch();
    rafraichirPagePanier();
    observerPanier();
    // Au chargement, on ne va chercher le panier que si le serveur a déjà constaté quelque chose à
    // corriger — une quantité de montage fausse, ou une ligne montage qui n'est pas la dernière
    // (cf. snippets/mauto-montage-pneu.liquid). Dans le cas normal, cette surveillance ne coûte
    // donc aucun appel réseau.
    if (Number(cfg.pneusAMonter) !== Number(cfg.montagesAuPanier)) {
      synchroniserPuisRafraichir();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
